import axios from 'axios';
import { notifications } from '@mantine/notifications';
import config from '../utils/config';
import { showErrorNotification } from '../utils/utils';
import { LocationHierarchyPayload, TaskPayload } from '../stores/types';
import { attachSessionExpiryInterceptor } from './apiAuthInterceptors';
import { getAuthToken } from '../utils/cookies';
import {
  buildProjectFileExportUrl,
  downloadBlobFile,
  ExportCacheExpiredError,
  parseContentDispositionFilename,
  ProjectFileDownloadManager,
} from './projectFileDownload';

export const transactionAxios = axios.create({
    baseURL: config.TransactionServerUrl,
    headers: {
        'cache-control': 'no-cache' // Disable caching for transaction server calls
    }
});

const successNotificationColor = 'teal';
const errorNotificationColor = 'red';
const defaultNotificationColor = '#005eff'

export function setTransactionServerAuthToken(token: string | null) {
    if (token) {
        transactionAxios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
        delete transactionAxios.defaults.headers.common.Authorization;
    }
}


export async function fetchAllTasks({ projectId }: { projectId: string; }) {
    try {
        const res = await transactionAxios.get(`dpr/apis/planning/fetch/allTasks?projectId=${projectId}`);
        // console.log(res.data);
        return res.data;
    }
    catch (err) {
        console.error(`Error response: ${err}`);
        showErrorNotification(err, `Sorry! Failed to fetch data.`);
        throw err;
    }
}

/** Relationship / scope discriminator for GET fetch/task (not Gantt task-type meta). */
export const FETCH_TASK_QUERY_TYPES = [
    'DIRECT_PARENT_TASK',
    'PARENT_TASKS',
    'CHILD_TASKS',
    'DIRECT_CHILD_TASK',
    'SIBLING_TASK',
    'DEPENDENT_TASK',
    'DEFAULT',
] as const;

export type FetchTaskQueryType = (typeof FETCH_TASK_QUERY_TYPES)[number];

export interface FetchTaskParams {
    projectId: string;
    /** Anchor task when resolving related tasks or loading by id. */
    taskId?: string;
    /**
     * How to resolve task(s) relative to `taskId` (or project when omitted).
     * Use `DEFAULT` to fetch the task record for the given `taskId`.
     */
    type?: FetchTaskQueryType;
}

/**
 * Fetch task(s) from planning API (GET with query params).
 * `type` controls relationship scope (e.g. DEFAULT = the task for `taskId`).
 */
export async function fetchTask(params: FetchTaskParams) {
    try {
        const queryParams: Record<string, string> = {
            projectId: params.projectId,
        };
        if (params.taskId != null && String(params.taskId).trim() !== '') {
            queryParams.taskId = String(params.taskId).trim();
        }
        if (params.type != null && String(params.type).trim() !== '') {
            queryParams.type = String(params.type).trim();
        }

        const res = await transactionAxios.get(`dpr/apis/planning/fetch/tasks`, {
            params: queryParams,
        });
        return res.data;
    } catch (err) {
        console.error(`Error fetching task: ${err}`);
        showErrorNotification(err, `Sorry! Failed to fetch task.`);
        throw err;
    }
}


export async function fetchAllLinks({ projectId }: { projectId: string; }) {
    try {
        const res = await transactionAxios.get(`dpr/apis/planning/fetch/tasks?projectId=${projectId}&type=DEPENDENT_TASK`);
        // console.log(res.data);
        return res.data;
    }
    catch (err) {
        console.error(`Error response: ${err}`);
        showErrorNotification(err, `Sorry! Failed to fetch data.`);
        throw err;
    }
}

export async function saveLocationHierarchy({
    locations
}: {
    locations: LocationHierarchyPayload[];
}) {
    try {
        const res = await transactionAxios.post(
            `dpr/apis/planning/task-locations`,
            locations
        );
        return res.data;
    }
    catch (err) {
        console.error(`Error saving location hierarchy: ${err}`);
        showErrorNotification(err, `Sorry! Failed to save location hierarchy.`);
        throw err;
    }
}

export async function updateTasks({
    affectedTasks,
    dependencies,
}: {
    affectedTasks: TaskPayload[];
    dependencies: any[];
}) {
    try {
        const res = await transactionAxios.put(
            `dpr/apis/planning/update/affectedTask`,
            { affectedTasks, dependencies },
        );
        return res.data;
    }
    catch (err) {
        console.error(`Error updating tasks: ${err}`);
        showErrorNotification(err, `Sorry! Failed to update tasks.`);
        throw err;
    }
}

/** POST body for `dpr/apis/planning/task` (create task). */
export interface AddNewTaskRequest {
    id: string;
    projectId: string;
    parentTaskId?: string;
    calendarId?: string;
    locationId?: string;
    uniqueId: string;
    name: string;
    /** Some backends accept taskName as an alias for name. */
    taskName?: string;
    outlineLevel: number;
    outlinePosition: number;
    taskMode?: string;
    milestone: boolean;
    summary: boolean;
    duration: number;
    start: string;
    finish: string;
    slack?: number;
    critical?: boolean;
    work?: number;
    cost?: number;
    actualStart?: string | null;
    actualFinish?: string | null;
    actualDuration?: number;
    actualWork?: number;
    actualCost?: number;
    remainingDuration?: number;
    remainingWork?: number;
    constraintDate?: string | null;
    deadline?: string | null;
    creationDate?: string;
    resume?: string | null;
    stop?: string | null;
    percentComplete?: number;
    workPercentComplete?: number;
    physicalPercentComplete?: number;
    fixedCost?: number;
    priority?: number;
    constraintType?: string;
    type?: string;
    active?: boolean;
    durationType?: string;
    notes?: string;
    meta?: Record<string, unknown>;
}

export interface PlanningTaskMutationResponse {
    status?: string;
    message?: string;
    requestId?: string;
    errorType?: string;
    statusCode?: number;
    data?: string;
    payload?: string;
    refId?: string;
    cacheExpiryHours?: number;
}

/** Normalized async job handle returned by planning mutation endpoints (import, linking, etc.). */
export interface PlanningAsyncJobHandle {
    requestId: string;
    refId: string;
    status?: string;
    message?: string;
}

function readMutationField(
    sources: Array<Record<string, unknown> | null | undefined>,
    keys: string[],
): string {
    for (const source of sources) {
        if (!source) continue;
        for (const key of keys) {
            const value = source[key];
            if (value != null && String(value).trim() !== '') {
                return String(value).trim();
            }
        }
    }
    return '';
}

/**
 * Parse planning async responses that return tracking ids under requestId and/or refId
 * (often wrapped: `{ status, data: { ... } }`).
 */
export function parsePlanningMutationResponse(payload: unknown): PlanningAsyncJobHandle {
    const root =
        payload != null && typeof payload === 'object'
            ? (payload as Record<string, unknown>)
            : {};
    const nested =
        root.data != null && typeof root.data === 'object' && !Array.isArray(root.data)
            ? (root.data as Record<string, unknown>)
            : null;
    const sources = [root, nested];

    const requestId = readMutationField(sources, ['requestId', 'requestID', 'request_id']);
    const refId = readMutationField(sources, ['refId', 'ref_id']);
    const trackingId = requestId || refId;

    return {
        requestId: trackingId,
        refId: refId || trackingId,
        status: readMutationField(sources, ['status']) || undefined,
        message: readMutationField(sources, ['message']) || undefined,
    };
}

/** POST body for `dpr/apis/planning/project` (bootstrap a new schedule). */
export interface CreateNewProjectRequest {
    id: string;
    title: string;
    statusDate: string;
    projectStart: string;
    projectFinish: string;
    minutesPerDay: number;
    minutesPerWeek: number;
    daysPerMonth: number;
    currencyCode: string;
    currencySymbol: string;
    currencyDigits: number;
    baselineForEV: number;
    meta?: Record<string, unknown>;
}

export async function createNewProject(
    payload: CreateNewProjectRequest,
    options?: { silent?: boolean },
) {
    try {
        const res = await transactionAxios.post<PlanningTaskMutationResponse>(
            'dpr/apis/planning/project',
            payload,
            { headers: { 'Content-Type': 'application/json' } },
        );
        if (!options?.silent) {
            notifications.show({
                title: 'Project created',
                message: res.data?.message || 'The project schedule was initialized.',
                color: successNotificationColor,
            });
        }
        return res.data;
    } catch (err) {
        console.error(`Error creating project: ${err}`);
        showErrorNotification(err, `Sorry! Failed to create project.`);
        throw err;
    }
}

/** Server-side schedule recalculate engines. */
export type ScheduleRecalculateEngine = 'MPXJ' | 'CPM';

export const SCHEDULE_RECALCULATE_ENGINES: Array<{
    id: ScheduleRecalculateEngine;
    name: string;
}> = [
    { id: 'MPXJ', name: 'MPXJ' },
    { id: 'CPM', name: 'CPM' },
];

export interface ScheduleRecalculateResponse {
    message?: string;
    data?: string;
    refId?: string;
    cacheExpiryHours?: number;
}

/**
 * POST dpr/apis/planning/project/{projectId}/recalculate
 * Body: { projectId, engine: "MPXJ" | "CPM" }
 */
export async function recalculateProjectSchedule(
    params: { projectId: string; engine: ScheduleRecalculateEngine },
    options?: { silent?: boolean },
): Promise<ScheduleRecalculateResponse> {
    const projectId = String(params.projectId ?? '').trim();
    const engine = params.engine;
    if (!projectId) {
        throw new Error('projectId is required to recalculate the schedule.');
    }
    if (engine !== 'MPXJ' && engine !== 'CPM') {
        throw new Error('engine must be MPXJ or CPM.');
    }
    try {
        const res = await transactionAxios.post<ScheduleRecalculateResponse>(
            `dpr/apis/planning/project/${encodeURIComponent(projectId)}/recalculate`,
            { projectId, engine },
            { headers: { 'Content-Type': 'application/json' } },
        );
        if (!options?.silent) {
            notifications.show({
                title: 'Schedule recalculated',
                message: res.data?.message || 'The project schedule was recalculated on the server.',
                color: successNotificationColor,
            });
        }
        return res.data ?? {};
    } catch (err) {
        console.error(`Error recalculating project schedule: ${err}`);
        showErrorNotification(err, `Sorry! Failed to recalculate schedule.`);
        throw err;
    }
}

export async function addNewTask(
    payload: AddNewTaskRequest | Record<string, unknown>,
    options?: { silent?: boolean },
) {
    try {
        const res = await transactionAxios.post<PlanningTaskMutationResponse>(
            'dpr/apis/planning/task',
            payload,
            { headers: { 'Content-Type': 'application/json' } },
        );
        if (!options?.silent) {
            notifications.show({
                title: 'Task created',
                message: res.data?.message || 'The new task was added to the schedule.',
                color: successNotificationColor,
            });
        }
        return res.data;
    } catch (err) {
        console.error(`Error creating task: ${err}`);
        showErrorNotification(err, `Sorry! Failed to create task.`);
        throw err;
    }
}

export async function deleteTask(
    { taskId }: { taskId: string },
    options?: { silent?: boolean },
) {
    try {
        const res = await transactionAxios.delete<PlanningTaskMutationResponse>(
            `dpr/apis/planning/task/${encodeURIComponent(taskId)}`,
        );
        if (!options?.silent) {
            notifications.show({
                title: 'Task deleted',
                message: res.data?.message || 'The task was removed from the schedule.',
                color: successNotificationColor,
            });
        }
        return res.data;
    } catch (err) {
        console.error(`Error deleting task: ${err}`);
        showErrorNotification(err, `Sorry! Failed to delete task.`);
        throw err;
    }
}

// --- Task dependency (predecessor/successor) APIs ---

export interface TaskDependencyRequest {
    id?: string;
    projectId: string;
    /** Successor task (the task that has the predecessor). */
    taskId: string;
    /** Predecessor task this dependency points at. */
    dependsOnTaskId: string;
    /** FS | SS | FF | SF */
    dependencyType: string;
    lag?: number;
    meta?: Record<string, unknown>;
}

export async function fetchTaskDependencies(params: {
    projectId: string;
    taskId?: string;
}) {
    try {
        const query = new URLSearchParams({ projectId: params.projectId });
        if (params.taskId?.trim()) {
            query.set('taskId', params.taskId.trim());
        }
        const res = await transactionAxios.get(
            `dpr/apis/planning/task/dependencies?${query.toString()}`,
        );
        return res.data;
    } catch (err) {
        console.error(`Error fetching task dependencies: ${err}`);
        showErrorNotification(err, `Sorry! Failed to fetch task dependencies.`);
        throw err;
    }
}

export async function createTaskDependency(
    payload: TaskDependencyRequest,
    options?: { silent?: boolean },
) {
    try {
        const res = await transactionAxios.post(
            'dpr/apis/planning/task/dependency',
            payload,
            { headers: { 'Content-Type': 'application/json' } },
        );
        if (!options?.silent) {
            notifications.show({
                title: 'Dependency created',
                message: res.data?.message || 'Task dependency was created.',
                color: successNotificationColor,
            });
        }
        return res.data;
    } catch (err) {
        console.error(`Error creating task dependency: ${err}`);
        showErrorNotification(err, `Sorry! Failed to create task dependency.`);
        throw err;
    }
}

export async function updateTaskDependency(
    dependencyId: string,
    payload: Omit<TaskDependencyRequest, 'id'>,
    options?: { silent?: boolean },
) {
    try {
        const res = await transactionAxios.put(
            `dpr/apis/planning/task/dependency/${encodeURIComponent(dependencyId)}`,
            payload,
            { headers: { 'Content-Type': 'application/json' } },
        );
        if (!options?.silent) {
            notifications.show({
                title: 'Dependency updated',
                message: res.data?.message || 'Task dependency was updated.',
                color: successNotificationColor,
            });
        }
        return res.data;
    } catch (err) {
        console.error(`Error updating task dependency: ${err}`);
        showErrorNotification(err, `Sorry! Failed to update task dependency.`);
        throw err;
    }
}

export async function deleteTaskDependency(
    { dependencyId }: { dependencyId: string },
    options?: { silent?: boolean },
) {
    try {
        const res = await transactionAxios.delete(
            `dpr/apis/planning/task/dependency/${encodeURIComponent(dependencyId)}`,
        );
        if (!options?.silent) {
            notifications.show({
                title: 'Dependency deleted',
                message: res.data?.message || 'Task dependency was removed.',
                color: successNotificationColor,
            });
        }
        return res.data;
    } catch (err) {
        console.error(`Error deleting task dependency: ${err}`);
        showErrorNotification(err, `Sorry! Failed to delete task dependency.`);
        throw err;
    }
}

// --- Location Hierarchy APIs ---

/** Flat location item from fetchAllLocationsForProject */
export interface LocationItem {
    id: string;
    projectId: string;
    name: string;
    locationType: string;
    locationLevel: number;
    locationGroup: string[] | null;
}

/** Tree node from fetchLocationsTreeByProject */
export interface LocationTreeNode {
    location_id: string;
    name: string;
    location_type: string;
    location_level: number;
    location_group: string[] | null;
    aliases?: string[] | null;
    locationGroup?: string[] | null;
    parent_location_id: string | null;
    meta: Record<string, unknown>;
    child: LocationTreeNode[];
}

function normalizeLocationTreeNode(raw: any): LocationTreeNode {
    const aliasesRaw =
        raw?.location_group ??
        raw?.aliases ??
        raw?.locationGroup ??
        [];
    const aliases = Array.isArray(aliasesRaw)
        ? aliasesRaw.map((a: any) => String(a).trim()).filter(Boolean)
        : [];
    const childRaw = Array.isArray(raw?.child) ? raw.child : [];
    return {
        location_id: String(raw?.location_id ?? raw?.locationId ?? ''),
        name: String(raw?.name ?? ''),
        location_type: String(raw?.location_type ?? raw?.locationType ?? 'project'),
        location_level: Number(raw?.location_level ?? raw?.locationLevel ?? 1),
        location_group: aliases,
        aliases,
        locationGroup: aliases,
        parent_location_id:
            raw?.parent_location_id != null
                ? String(raw.parent_location_id)
                : raw?.parentLocationId != null
                    ? String(raw.parentLocationId)
                    : null,
        meta: (raw?.meta && typeof raw.meta === 'object') ? raw.meta : {},
        child: childRaw.map(normalizeLocationTreeNode),
    };
}

/** Payload for updateLocation */
export interface UpdateLocationPayload {
    id: string;
    projectId: string;
    name: string;
    locationType: string;
    locationLevel: number;
    parentLocationId: string | null;
    /** Preferred key for backend alias list. */
    aliases?: string[];
    /** Backward-compatible alias key used by older callers. */
    locationGroup?: string[];
    meta?: Record<string, unknown>;
}

export async function fetchAllLocationsForProject({ projectId }: { projectId: string }) {
    try {
        const res = await transactionAxios.get<{ data: LocationItem[] }>(
            `dpr/apis/planning/fetch/allLocations?projectId=${projectId}`
        );
        return res.data;
    } catch (err) {
        console.error(`Error fetching locations: ${err}`);
        showErrorNotification(err, `Sorry! Failed to fetch locations.`);
        throw err;
    }
}

/** In-flight promise cache: one request per projectId at a time to avoid duplicate network calls */
const locationsTreeFetchByProjectId = new Map<string, Promise<{ data: LocationTreeNode[] }>>();

export async function fetchLocationsTreeByProject({ projectId }: { projectId: string }) {
    const existing = locationsTreeFetchByProjectId.get(projectId);
    if (existing) return existing;

    const promise = (async () => {
        try {
            const res = await transactionAxios.get<{
                status: string;
                message: string;
                data: LocationTreeNode[];
            }>(`dpr/apis/planning/fetch/locations/tree?projectId=${projectId}`);
            const normalizedData = Array.isArray(res.data?.data)
                ? res.data.data.map(normalizeLocationTreeNode)
                : [];
            return {
                ...res.data,
                data: normalizedData,
            };
        } catch (err) {
            console.error(`Error fetching locations tree: ${err}`);
            showErrorNotification(err, `Sorry! Failed to fetch location hierarchy.`);
            throw err;
        } finally {
            locationsTreeFetchByProjectId.delete(projectId);
        }
    })();

    locationsTreeFetchByProjectId.set(projectId, promise);
    return promise;
}

export async function updateLocation(payload: UpdateLocationPayload[]) {
    try {
        const normalizedPayload = payload.map((item) => {
            const aliases = Array.isArray(item.aliases)
                ? item.aliases
                : Array.isArray(item.locationGroup)
                    ? item.locationGroup
                    : [];
            return {
                ...item,
                aliases,
            };
        });
        const res = await transactionAxios.put(
            `dpr/apis/planning/update/location`,
            normalizedPayload
        );
        return res.data;
    } catch (err) {
        console.error(`Error updating location: ${err}`);
        showErrorNotification(err, `Sorry! Failed to update location.`);
        throw err;
    }
}

export async function deleteLocation({
    projectId,
    locationIds,
}: {
    projectId: string;
    locationIds: string[];
}) {
    try {
        const res = await transactionAxios.delete(`dpr/apis/planning/delete/location`, {
            data: { projectId, locationIds },
        });
        return res.data;
    } catch (err) {
        console.error(`Error deleting location(s): ${err}`);
        showErrorNotification(err, `Sorry! Failed to delete location(s).`);
        throw err;
    }
}

// --- Task meta (type + location) API ---

/** Single item for updateTaskMeta payload. Backend expects locationId only when type is "Location". */
export interface UpdateTaskMetaItem {
    taskId: string;
    type: string;
    /** Send only when type is "Location"; omit for other task types. */
    locationId?: string;
}

/** Response from updateTaskMeta */
export interface UpdateTaskMetaResponse {
    message?: string;
}

/**
 * Update task meta (type and location) for one or more tasks.
 * On success, caller should update local task store (taskType, locationType, locationId).
 * Pass `{ silent: true }` to suppress the error toast (caller aggregates feedback).
 */
export async function updateTaskMeta(
    payload: UpdateTaskMetaItem[],
    options?: { silent?: boolean },
): Promise<UpdateTaskMetaResponse> {
    try {
        const res = await transactionAxios.put<UpdateTaskMetaResponse>(
            `dpr/apis/planning/update/task-meta-type`,
            payload
        );
        return res.data ?? { message: 'Task meta successfully updated' };
    } catch (err) {
        console.error(`Error updating task meta: ${err}`);
        if (!options?.silent) {
            showErrorNotification(err, `Sorry! Failed to update task meta.`);
        }
        throw err;
    }
}

// --- Import MS-Project (WBS) API ---

export type ImportWbsSetupMode = 'merge' | 'replace';

export interface ImportWbsFromProjectFileParams {
    projectId: string;
    file: File;
    /** Defaults to `merge`. Use `replace` after a CONFLICT confirmation. */
    mode?: ImportWbsSetupMode;
}

export interface ImportWbsFromProjectFileResponse {
    /** Id used by Request Tracker (`requestID` / `requestId` on backend). */
    requestId: string;
    /** Legacy/async cache id when backend also exposes refId. */
    refId: string;
}

/** True when setup rejects because the project already has planning data (HTTP 409 + CONFLICT). */
export function isImportPlanningConflictError(err: unknown): boolean {
    if (!axios.isAxiosError(err) || err.response?.status !== 409) {
        return false;
    }
    const data = err.response?.data;
    if (!data || typeof data !== 'object') {
        return false;
    }
    const record = data as Record<string, unknown>;
    const errorType = record.errorType ?? record.error_type;
    return String(errorType ?? '').toUpperCase() === 'CONFLICT';
}

/**
 * Upload an MS-Project file (WBS) for a given project.
 * The server responds quickly with a refId that can be used to track
 * the long-running import job on the backend.
 *
 * Query param `mode` is `merge` (default) or `replace`.
 * On HTTP 409 with `errorType: CONFLICT`, no error toast is shown — callers should prompt to replace.
 */
export async function importWBSFromProjectFile(
    params: ImportWbsFromProjectFileParams,
): Promise<ImportWbsFromProjectFileResponse> {
    const { projectId, file, mode = 'merge' } = params;
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('file', file);

    try {
        const res = await transactionAxios.post(
            `dpr/apis/planning/setup`,
            formData,
            {
                params: { mode },
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        const parsed = parsePlanningMutationResponse(res.data);
        if (!parsed.requestId) {
            throw new Error('Import started but the server did not return a request tracking id.');
        }
        return { requestId: parsed.requestId, refId: parsed.refId };
    } catch (err) {
        console.error(`Error importing MS-Project file: ${err}`);
        if (isImportPlanningConflictError(err)) {
            throw err;
        }
        showErrorNotification(err, `Sorry! Failed to import MS-Project data.`);
        throw err;
    }
}

export interface ExportProjectFileParams {
    projectId: string;
    onProgress?: (percent: number) => void;
}

export interface ExportLookAheadExcelParams {
    projectId: string;
    /** YYYY-MM-DD — look-ahead window start */
    startWindow: string;
    /** YYYY-MM-DD — look-ahead window end */
    endWindow: string;
    /** Match window start date in the result set (API default: true) */
    matchStart?: boolean;
    /** Match window end date in the result set (API default: true) */
    matchEnd?: boolean;
}

export type ExportLookAheadPdfParams = ExportLookAheadExcelParams;

export interface ExportScheduleExcelParams {
    projectId: string;
}

function buildLookAheadExportQuery(params: ExportLookAheadExcelParams): URLSearchParams {
    const { projectId, startWindow, endWindow, matchStart = true, matchEnd = true } = params;
    if (!projectId?.trim()) {
        throw new Error('projectId is required for look-ahead export.');
    }
    if (!startWindow?.trim() || !endWindow?.trim()) {
        throw new Error('startWindow and endWindow are required for look-ahead export.');
    }

    return new URLSearchParams({
        projectId: projectId.trim(),
        startWindow: startWindow.trim(),
        endWindow: endWindow.trim(),
        matchStart: String(matchStart),
        matchEnd: String(matchEnd),
    });
}

async function downloadPlanningExportFile(options: {
    path: string;
    query: URLSearchParams;
    fallbackFilename: string;
    jsonErrorFallback: string;
    errorTitle: string;
    logLabel: string;
}): Promise<void> {
    const { path, query, fallbackFilename, jsonErrorFallback, errorTitle, logLabel } = options;
    try {
        const res = await transactionAxios.get(`${path}?${query.toString()}`, {
            responseType: 'blob',
        });

        const contentType = String(res.headers?.['content-type'] ?? '');
        // Some gateways return 200 with a JSON error body as a blob.
        if (contentType.includes('application/json')) {
            const text = await (res.data as Blob).text();
            let message = jsonErrorFallback;
            try {
                const parsed = JSON.parse(text) as { message?: string; error?: string; detail?: string };
                message = parsed.message || parsed.error || parsed.detail || message;
            } catch {
                if (text.trim()) message = text.trim();
            }
            throw new Error(message);
        }

        const filename =
            parseContentDispositionFilename(res.headers?.['content-disposition']) || fallbackFilename;
        downloadBlobFile(res.data as Blob, filename);

        notifications.show({
            title: 'Export complete',
            message: `Downloaded ${filename}`,
            color: successNotificationColor,
        });
    } catch (err) {
        console.error(`Error exporting ${logLabel}: ${err}`);
        // Blob error bodies (400 validation, etc.) are resolved inside showErrorNotification.
        showErrorNotification(err, errorTitle);
        throw err;
    }
}

/**
 * Export look-ahead schedule as Excel
 * GET /dpr/apis/planning/look-ahead/excel
 */
export async function exportLookAheadExcel(params: ExportLookAheadExcelParams): Promise<void> {
    await downloadPlanningExportFile({
        path: 'dpr/apis/planning/look-ahead/excel',
        query: buildLookAheadExportQuery(params),
        fallbackFilename: `look-ahead-${params.projectId}.xlsx`,
        jsonErrorFallback: 'Failed to export look-ahead Excel.',
        errorTitle: 'Look-ahead Excel export failed',
        logLabel: 'look-ahead Excel',
    });
}

/**
 * Export look-ahead schedule as PDF
 * GET /dpr/apis/planning/look-ahead/pdf
 */
export async function exportLookAheadPdf(params: ExportLookAheadPdfParams): Promise<void> {
    await downloadPlanningExportFile({
        path: 'dpr/apis/planning/look-ahead/pdf',
        query: buildLookAheadExportQuery(params),
        fallbackFilename: `look-ahead-${params.projectId}.pdf`,
        jsonErrorFallback: 'Failed to export look-ahead PDF.',
        errorTitle: 'Look-ahead PDF export failed',
        logLabel: 'look-ahead PDF',
    });
}

/**
 * Export full project schedule as Excel
 * GET /dpr/apis/planning/schedule/excel
 */
export async function exportScheduleExcel(params: ExportScheduleExcelParams): Promise<void> {
    const { projectId } = params;
    if (!projectId?.trim()) {
        throw new Error('projectId is required for schedule Excel export.');
    }

    const qs = new URLSearchParams({ projectId: projectId.trim() });
    await downloadPlanningExportFile({
        path: 'dpr/apis/planning/schedule/excel',
        query: qs,
        fallbackFilename: `schedule-${projectId}.xlsx`,
        jsonErrorFallback: 'Failed to export schedule Excel.',
        errorTitle: 'Schedule Excel export failed',
        logLabel: 'schedule Excel',
    });
}

/**
 * Export the current project plan file via chunked HTTP Range requests.
 */
export async function exportProjectFile(params: ExportProjectFileParams): Promise<void> {
    const { projectId, onProgress } = params;
    const baseUrl = config.TransactionServerUrl;
    if (!baseUrl?.trim()) {
        throw new Error('Transaction server URL is not configured.');
    }

    try {
        const url = buildProjectFileExportUrl(projectId, baseUrl);
        const manager = new ProjectFileDownloadManager({
            authToken: getAuthToken(),
            onProgress: onProgress
                ? (downloaded, total) => {
                    if (total > 0) {
                        onProgress(Math.min(100, Math.round((downloaded / total) * 100)));
                    }
                }
                : undefined,
        });

        const { filename: rawFilename } = await manager.download(url, `project-${projectId}.xml`);
        const filename = rawFilename.toLowerCase().endsWith('.mpp')
          ? `${rawFilename.slice(0, -4)}.xml`
          : rawFilename;

        notifications.show({
            title: 'Export complete',
            message: `Downloaded ${filename}`,
            color: successNotificationColor,
        });
    } catch (err) {
        console.error(`Error exporting project file: ${err}`);
        if (err instanceof ExportCacheExpiredError) {
            notifications.show({
                title: 'Export expired',
                message: err.message,
                color: errorNotificationColor,
            });
            throw err;
        }
        showErrorNotification(err, 'Sorry! Failed to export project file.');
        throw err;
    }
}

// --- Reassign location to task ---

export interface ReassignLocationToTaskPayload {
    projectId: string;
    /** Optional — when omitted, server loads the saved project location hierarchy. */
    location_hierarchy?: LocationTreeNode[];
    /** Optional — when omitted, server loads project tasks. */
    tasks?: ReassignTaskItem[];
}

/** Task item for reassignLocationToTask (includes parent_task_id). */
export interface ReassignTaskItem extends ClassificationTaskItem {
    parent_task_id: string | null;
}

export interface ReassignLocationToTaskResponse {
    message?: string;
    [key: string]: any;
}

/**
 * Server-side task–location reassignment.
 * Pass only `projectId` to reassign from persisted locations/tasks.
 * Pass `location_hierarchy` + `tasks` (import wizard) to upsert hierarchy then remap.
 */
export async function reassignLocationToTask(
    payload: ReassignLocationToTaskPayload
): Promise<ReassignLocationToTaskResponse> {
    const projectId = String(payload.projectId ?? '').trim();
    if (!projectId) {
        throw new Error('projectId is required to reassign locations to tasks.');
    }
    const body: Record<string, unknown> = { projectId };
    if (payload.location_hierarchy != null) {
        body.location_hierarchy = payload.location_hierarchy;
    }
    if (payload.tasks != null) {
        body.tasks = payload.tasks;
    }
    try {
        const res = await transactionAxios.post<ReassignLocationToTaskResponse>(
            `dpr/apis/planning/reassign/location-to-task`,
            body
        );
        return res.data ?? {};
    } catch (err) {
        console.error(`Error reassigning location to task: ${err}`);
        showErrorNotification(err, `Sorry! Failed to reassign location-to-task mapping.`);
        throw err;
    }
}

/**
 * Set UOM and quantities. Omitted fields stay unchanged. The response has no task body;
 * callers refetch with fetchTask.
 */
export async function updateTaskQuantity(taskId: string, body: UpdateTaskQuantityRequest) {
    try {
        const res = await transactionAxios.put<PlanningTaskMutationResponse>(
            `dpr/apis/planning/task/${encodeURIComponent(taskId)}/quantity`,
            body,
            { headers: { 'Content-Type': 'application/json' } },
        );
        return res.data;
    } catch (err) {
        showErrorNotification(err, 'Sorry! Failed to update task quantity.');
        throw err;
    }
}

// --- Convert task to WBS (unit-activities sync) ---

export type ActivityTypeMode = 'match' | 'override' | 'create_new';

export interface ConvertTaskToWbsPayload {
    projectId: string;
    taskId: string;
    /** Omitted means the backend default, `match`. */
    activityTypeMode?: ActivityTypeMode;
}

export interface UpdateTaskQuantityRequest {
    projectId: string;
    uom?: string;
    totalQuantity?: number;
    achievedQuantity?: number;
}

export interface ConvertTaskToWbsResponse {
    message: string;
    data: string;
    refId: string;
    cacheExpiryHours: number;
}

/**
 * Trigger backend conversion from task to WBS (unit-activities).
 * This queues/executes conversion for the given task context in a project.
 * Sends `projectId` and `taskId` as URL query parameters.
 */
export async function convertTaskToWBS(
    payload: ConvertTaskToWbsPayload
): Promise<ConvertTaskToWbsResponse> {
    try {
        const res = await transactionAxios.post<ConvertTaskToWbsResponse>(
            `dpr/apis/planning/convert/task-to-wbs`,
            undefined,
            {
                params: {
                    projectId: payload.projectId,
                    taskId: payload.taskId,
                    ...(payload.activityTypeMode
                        ? { activityTypeMode: payload.activityTypeMode }
                        : {}),
                },
            }
        );
        return res.data;
    } catch (err) {
        console.error(`Error converting task to WBS: ${err}`);
        showErrorNotification(err, `Sorry! Failed to convert task to WBS.`);
        throw err;
    }
}

// --- Location management chat agent ---

export interface LocationChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

export interface LocationChatProposedAction {
    op: 'create' | 'update' | 'delete' | 'duplicate';
    locationId?: string | null;
    parentLocationId?: string | null;
    name?: string | null;
    locationType?: string | null;
    locationLevel?: number | null;
    aliases?: string[] | null;
    floorNumber?: number | null;
    summary: string;
}

/**
 * Flat location row describing the tree the client is currently looking at. May include unsaved
 * draft edits, which is the point: without it the agent can only see saved locations.
 */
export interface LocationChatSnapshotItem {
    id: string;
    projectId: string;
    name: string;
    locationType: string;
    locationLevel: number;
    parentLocationId: string | null;
    aliases: string[];
}

/**
 * The agent asking us to run task classification and load the result into the draft.
 *
 * An instruction, not a payload: classification reads every task in the project and is far too slow
 * and too large to travel through a chat turn, so we call locationClassification ourselves. The
 * agent still sees the outcome, because the draft tree goes back to it on the next message.
 */
export interface LocationChatClassifyRequest {
    mode: 'replace' | 'merge';
    projectType?: string | null;
}

export interface LocationChatResult {
    reply: string;
    proposedActions: LocationChatProposedAction[];
    appliedActions: string[];
    history: LocationChatMessage[];
    classifyRequest: LocationChatClassifyRequest | null;
}

/**
 * One turn with the conversational location agent. Stateless - `history` is echoed back each turn.
 *
 * Proposals are applied into the client-side location draft rather than server-side, so the server's
 * `pendingConfirmation` field is deliberately not part of this signature.
 */
export async function locationChat({
    projectId,
    message,
    history = [],
    locations,
}: {
    projectId: string;
    message: string;
    history?: LocationChatMessage[];
    locations?: LocationChatSnapshotItem[];
}): Promise<LocationChatResult> {
    try {
        const res = await transactionAxios.post<{ status?: string; message?: string; data?: any }>(
            `dpr/apis/planning/location/chat`,
            { projectId, message, history, ...(locations?.length ? { locations } : {}) }
        );
        const data = res.data?.data ?? res.data;
        return {
            reply: String(data?.reply ?? ''),
            proposedActions: Array.isArray(data?.proposedActions) ? data.proposedActions : [],
            appliedActions: Array.isArray(data?.appliedActions) ? data.appliedActions.map(String) : [],
            history: Array.isArray(data?.history) ? data.history : [],
            classifyRequest:
                data?.classifyRequest?.mode === 'replace' || data?.classifyRequest?.mode === 'merge'
                    ? { mode: data.classifyRequest.mode, projectType: data.classifyRequest.projectType ?? null }
                    : null,
        };
    } catch (err) {
        console.error(`Error in location chat: ${err}`);
        showErrorNotification(err, `Sorry! Failed to reach the location assistant.`);
        throw err;
    }
}

// --- Location classification (AI) API ---

/** Task item from locationClassification response (meta contains type and optional location_id). */
export interface ClassificationTaskItem {
    task_id: string;
    task_name: string;
    meta: {
        type?: string;
        location_id?: string;
    };
}

/** Response from locationClassification API. */
export interface LocationClassificationResponse {
    tasks: ClassificationTaskItem[];
    location_hierarchy: LocationTreeNode[];
}

/** Normalize a single task from the API so it always has task_id and meta (type, location_id). Exported for reuse in reassign flow. */
export function normalizeClassificationTask(raw: any): ClassificationTaskItem {
    const taskId = raw?.task_id ?? raw?.taskId ?? raw?.id ?? '';
    const meta = raw?.meta ?? raw?.Meta ?? {};
    const typeRaw = meta?.type ?? meta?.Type ?? meta?.task_type ?? meta?.taskType ?? raw?.task_type ?? raw?.taskType;
    const typeStr = typeRaw != null && typeRaw !== '' ? String(typeRaw).trim() : undefined;
    const locationIdRaw = meta?.location_id ?? meta?.locationId;
    const locationIdStr = locationIdRaw != null && locationIdRaw !== '' ? String(locationIdRaw).trim() : undefined;
    return {
        task_id: String(taskId),
        task_name: raw?.task_name ?? raw?.taskName ?? raw?.text ?? '',
        meta: {
            type: typeStr,
            location_id: locationIdStr,
        },
    };
}

/** Normalize a task to ReassignTaskItem (includes parent_task_id) for reassignLocationToTask. */
export function normalizeTaskForReassign(raw: any): ReassignTaskItem {
    const base = normalizeClassificationTask(raw);
    const parentId = raw?.parent_task_id ?? raw?.parentTaskId ?? null;
    return {
        ...base,
        parent_task_id: parentId != null ? String(parentId) : null,
    };
}

/**
 * Normalize classification response: accept snake_case or camelCase, optional data wrapper, and normalize each task.
 */
function normalizeClassificationResponse(raw: any): LocationClassificationResponse {
    const payload = raw?.data != null ? raw.data : raw;
    const tasksRaw = payload?.tasks ?? payload?.Tasks ?? [];
    const tasksArr = Array.isArray(tasksRaw) ? tasksRaw : [];
    const location_hierarchy = payload?.location_hierarchy ?? payload?.locationHierarchy ?? [];
    const hierarchyArr = Array.isArray(location_hierarchy) ? location_hierarchy : [];
    const tasks = tasksArr.map(normalizeClassificationTask);
    return { tasks, location_hierarchy: hierarchyArr };
}

/**
 * Fetch AI-suggested location classification for all tasks in a project.
 * Returns proposed location hierarchy and task–location assignments for user review.
 */
export async function locationClassification({ projectId }: { projectId: string }): Promise<LocationClassificationResponse> {
    try {
        const res = await transactionAxios.get(
            `dpr/apis/planning/fetch/tasks/classification?projectId=${projectId}`
        );
        return normalizeClassificationResponse(res.data);
    } catch (err) {
        console.error(`Error fetching location classification: ${err}`);
        showErrorNotification(err, `Sorry! Failed to fetch location classification.`);
        throw err;
    }
}

// --- Calendar Management APIs ---

export interface CalendarMeta {
    [key: string]: unknown;
}

export interface CalendarRecord {
    id: string;
    name: string;
    projectId: string;
    type: string;
    isDefault: boolean;
    timezone: string;
    active?: boolean;
    meta?: CalendarMeta;
}

export interface CalendarWeekRecord {
    id: string;
    calendarId: string;
    dayOfWeek: number;
    shiftNumber?: number;
    isWorking: boolean;
    dayStartTime: string;
    dayEndTime: string;
    workingMinutes: number;
}

export interface CalendarExceptionRecord {
    id: string;
    calendarId: string;
    exceptionDate: string;
    isWorking: boolean;
    workingMinutes: number;
    description?: string;
}

export interface CalendarDayRecord {
    id: string;
    calendarId: string;
    calendarDate: string;
    isWorking: boolean;
    workingMinutes: number;
    reason?: string;
}

export interface CalendarDetailsResponse {
    calendar?: CalendarRecord | null;
    weeks: CalendarWeekRecord[];
    exceptions: CalendarExceptionRecord[];
    days: CalendarDayRecord[];
}

function calendarsFromDetailsPayload(payload: unknown): CalendarRecord[] {
    if (payload == null) return [];

    if (Array.isArray(payload)) {
        return payload
            .map((item: any) => item?.calendar ?? item)
            .filter((item: any) => item && typeof item === 'object')
            .map(normalizeCalendarRecord)
            .filter((c) => c.id.length > 0);
    }

    if (typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        if (Array.isArray(obj.calendars)) {
            return obj.calendars
                .filter((item: any) => item && typeof item === 'object')
                .map((item: any) => normalizeCalendarRecord(item?.calendar ?? item))
                .filter((c) => c.id.length > 0);
        }
        if (obj.calendar && typeof obj.calendar === 'object') {
            const cal = normalizeCalendarRecord(obj.calendar);
            return cal.id.length > 0 ? [cal] : [];
        }
        if (obj.id != null || obj.calendarId != null || obj.calendar_id != null) {
            const cal = normalizeCalendarRecord(obj);
            return cal.id.length > 0 ? [cal] : [];
        }
    }

    return [];
}

export async function fetchProjectCalendars(projectId: string): Promise<CalendarRecord[]> {
    try {
        const res = await transactionAxios.get(
            `dpr/apis/planning/calendar/details?projectId=${encodeURIComponent(projectId)}`
        );
        const payload = res.data?.data ?? res.data?.payload ?? res.data;
        return calendarsFromDetailsPayload(payload);
    } catch (err) {
        // Some environments may not expose a list endpoint yet; fail gracefully.
        console.warn('[Calendar] List endpoint unavailable or failed:', err);
        return [];
    }
}

function normalizeCalendarRecord(raw: any): CalendarRecord {
    return {
        id: String(raw?.id ?? raw?.calendarId ?? raw?.calendar_id ?? ''),
        name: String(raw?.name ?? ''),
        projectId: String(raw?.projectId ?? raw?.project_id ?? ''),
        type: String(raw?.type ?? 'Standard'),
        isDefault: Boolean(raw?.isDefault ?? raw?.is_default ?? false),
        timezone: String(raw?.timezone ?? 'UTC'),
        active: raw?.active != null ? Boolean(raw.active) : undefined,
        meta: (raw?.meta && typeof raw.meta === 'object') ? raw.meta : {},
    };
}

function normalizeCalendarWeekRecord(raw: any): CalendarWeekRecord {
    const calendarId = String(raw?.calendarId ?? raw?.calendar_id ?? '');
    const dayOfWeek = Number(raw?.dayOfWeek ?? raw?.day_of_week ?? 1);
    const shiftNumber = raw?.shiftNumber != null ? Number(raw.shiftNumber) : undefined;
    return {
        // Some APIs do not return a row id for week rules; synthesize a stable fallback id.
        id: String(raw?.id ?? `${calendarId}:${dayOfWeek}:${shiftNumber ?? 1}`),
        calendarId,
        dayOfWeek,
        shiftNumber,
        isWorking: Boolean(raw?.isWorking ?? raw?.is_working ?? false),
        dayStartTime: String(raw?.dayStartTime ?? raw?.day_start_time ?? '08:00:00'),
        dayEndTime: String(raw?.dayEndTime ?? raw?.day_end_time ?? '17:00:00'),
        workingMinutes: Number(raw?.workingMinutes ?? raw?.working_minutes ?? 0),
    };
}

function normalizeCalendarExceptionRecord(raw: any): CalendarExceptionRecord {
    return {
        id: String(raw?.id ?? ''),
        calendarId: String(raw?.calendarId ?? raw?.calendar_id ?? ''),
        exceptionDate: String(raw?.exceptionDate ?? raw?.exception_date ?? ''),
        isWorking: Boolean(raw?.isWorking ?? raw?.is_working ?? false),
        workingMinutes: Number(raw?.workingMinutes ?? raw?.working_minutes ?? 0),
        description: raw?.description != null ? String(raw.description) : '',
    };
}

function normalizeCalendarDayRecord(raw: any): CalendarDayRecord {
    return {
        id: String(raw?.id ?? ''),
        calendarId: String(raw?.calendarId ?? raw?.calendar_id ?? ''),
        calendarDate: String(raw?.calendarDate ?? raw?.calendar_date ?? ''),
        isWorking: Boolean(raw?.isWorking ?? raw?.is_working ?? false),
        workingMinutes: Number(raw?.workingMinutes ?? raw?.working_minutes ?? 0),
        reason: raw?.reason != null ? String(raw.reason) : '',
    };
}

function normalizeCalendarDetailsResponse(raw: any): CalendarDetailsResponse {
    const payload = raw?.data ?? raw?.payload ?? raw;
    // Some environments return details as an array like:
    // [{ calendar: {...}, weekRules: [...], exceptionRules: [...], dayRules: [...] }, ...]
    if (Array.isArray(payload)) {
        const firstEntry = payload[0] ?? null;
        const first = firstEntry?.calendar ?? firstEntry ?? null;
        const weeksRaw = firstEntry?.weekRules ?? firstEntry?.weeks ?? firstEntry?.calendarWeeks ?? [];
        const exceptionsRaw =
            firstEntry?.exceptionRules ?? firstEntry?.exceptions ?? firstEntry?.calendarExceptions ?? [];
        const daysRaw = firstEntry?.dayRules ?? firstEntry?.days ?? firstEntry?.calendarDays ?? [];
        return {
            calendar: first ? normalizeCalendarRecord(first) : null,
            weeks: Array.isArray(weeksRaw) ? weeksRaw.map(normalizeCalendarWeekRecord) : [],
            exceptions: Array.isArray(exceptionsRaw) ? exceptionsRaw.map(normalizeCalendarExceptionRecord) : [],
            days: Array.isArray(daysRaw) ? daysRaw.map(normalizeCalendarDayRecord) : [],
        };
    }
    const calendarRaw = payload?.calendar ?? payload?.Calendar ?? payload?.root ?? payload?.details ?? null;
    const weeksRaw = payload?.weeks ?? payload?.calendarWeeks ?? payload?.week ?? [];
    const exceptionsRaw = payload?.exceptions ?? payload?.calendarExceptions ?? payload?.exception ?? [];
    const daysRaw = payload?.days ?? payload?.calendarDays ?? payload?.day ?? [];

    return {
        calendar: calendarRaw ? normalizeCalendarRecord(calendarRaw) : null,
        weeks: Array.isArray(weeksRaw) ? weeksRaw.map(normalizeCalendarWeekRecord) : [],
        exceptions: Array.isArray(exceptionsRaw) ? exceptionsRaw.map(normalizeCalendarExceptionRecord) : [],
        days: Array.isArray(daysRaw) ? daysRaw.map(normalizeCalendarDayRecord) : [],
    };
}

export async function createCalendar(payload: Omit<CalendarRecord, 'id'> & { id?: string }) {
    try {
        const res = await transactionAxios.post(`dpr/apis/planning/calendar`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error creating calendar: ${err}`);
        showErrorNotification(err, `Sorry! Failed to create calendar.`);
        throw err;
    }
}

export async function updateCalendar(calendarId: string, payload: Omit<CalendarRecord, 'id'>) {
    try {
        const res = await transactionAxios.put(`dpr/apis/planning/calendar/${calendarId}`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error updating calendar: ${err}`);
        showErrorNotification(err, `Sorry! Failed to update calendar.`);
        throw err;
    }
}

export async function deleteCalendar(calendarId: string) {
    try {
        const res = await transactionAxios.delete(`dpr/apis/planning/calendar/${calendarId}`);
        return res.data;
    } catch (err) {
        console.error(`Error deleting calendar: ${err}`);
        showErrorNotification(err, `Sorry! Failed to delete calendar.`);
        throw err;
    }
}

export async function fetchCalendarDetails({
    projectId,
    calendarId,
}: {
    projectId: string;
    calendarId: string;
}): Promise<CalendarDetailsResponse> {
    try {
        const res = await transactionAxios.get(
            `dpr/apis/planning/calendar/details?projectId=${encodeURIComponent(projectId)}&calendarId=${encodeURIComponent(calendarId)}`
        );
        return normalizeCalendarDetailsResponse(res.data);
    } catch (err) {
        console.error(`Error fetching calendar details: ${err}`);
        showErrorNotification(err, `Sorry! Failed to fetch calendar details.`);
        throw err;
    }
}

/**
 * Compact DHTMLX-shaped calendars for Live Schedule (pass-through to gantt.addCalendar).
 * GET /dpr/apis/planning/calendar/dhtmlx?projectId=
 * weekdays[0] = Sunday … [6] = Saturday; dates values are false | hour ranges.
 */
export type CompactDhtmlxDateWorktime = boolean | string[];

export interface CompactDhtmlxCalendarWorktime {
    hours: string[];
    days: {
        weekdays: number[];
        dates?: Record<string, CompactDhtmlxDateWorktime>;
    };
}

export interface CompactDhtmlxCalendar {
    id: string;
    worktime: CompactDhtmlxCalendarWorktime;
}

function normalizeCompactDhtmlxCalendar(raw: any): CompactDhtmlxCalendar | null {
    const id = String(raw?.id ?? raw?.calendarId ?? raw?.calendar_id ?? '').trim();
    const worktime = raw?.worktime ?? raw?.workTime;
    const hours = worktime?.hours;
    const weekdays = worktime?.days?.weekdays ?? worktime?.days;
    if (!id || !Array.isArray(hours) || !Array.isArray(weekdays) || weekdays.length !== 7) {
        return null;
    }
    const datesRaw = worktime?.days?.dates;
    const dates: Record<string, CompactDhtmlxDateWorktime> | undefined =
        datesRaw && typeof datesRaw === 'object' && !Array.isArray(datesRaw)
            ? (datesRaw as Record<string, CompactDhtmlxDateWorktime>)
            : undefined;
    return {
        id,
        worktime: {
            hours: hours.map((h: unknown) => String(h)),
            days: {
                weekdays: weekdays.map((d: unknown) => Number(d)),
                ...(dates ? { dates } : {}),
            },
        },
    };
}

function compactCalendarsFromPayload(payload: unknown): CompactDhtmlxCalendar[] {
    if (payload == null) return [];
    const list = Array.isArray(payload)
        ? payload
        : typeof payload === 'object' && Array.isArray((payload as any).calendars)
          ? (payload as any).calendars
          : typeof payload === 'object' && Array.isArray((payload as any).data)
            ? (payload as any).data
            : [];
    const out: CompactDhtmlxCalendar[] = [];
    for (const item of list) {
        const normalized = normalizeCompactDhtmlxCalendar(item);
        if (normalized) out.push(normalized);
    }
    return out;
}

/** Project calendars in DHTMLX addCalendar shape (Live Schedule work_time). */
export async function fetchProjectDhtmlxCalendars(
    projectId: string,
): Promise<CompactDhtmlxCalendar[]> {
    try {
        const res = await transactionAxios.get(
            `dpr/apis/planning/calendar/dhtmlx?projectId=${encodeURIComponent(projectId)}`,
        );
        const payload = res.data?.data ?? res.data?.payload ?? res.data;
        return compactCalendarsFromPayload(payload);
    } catch (err) {
        console.warn('[Calendar] DHTMLX compact calendars unavailable or failed:', err);
        return [];
    }
}

export async function createCalendarWeek(payload: Omit<CalendarWeekRecord, 'id'> & { id?: string }) {
    try {
        const res = await transactionAxios.post(`dpr/apis/planning/calendar/week`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error creating calendar week: ${err}`);
        showErrorNotification(err, `Sorry! Failed to create weekly rule.`);
        throw err;
    }
}

export async function updateCalendarWeek(calendarWeekId: string, payload: Omit<CalendarWeekRecord, 'id'>) {
    try {
        const res = await transactionAxios.put(`dpr/apis/planning/calendar/week/${calendarWeekId}`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error updating calendar week: ${err}`);
        showErrorNotification(err, `Sorry! Failed to update weekly rule.`);
        throw err;
    }
}

export async function deleteCalendarWeek(calendarWeekId: string) {
    try {
        const res = await transactionAxios.delete(`dpr/apis/planning/calendar/week/${calendarWeekId}`);
        return res.data;
    } catch (err) {
        console.error(`Error deleting calendar week: ${err}`);
        showErrorNotification(err, `Sorry! Failed to delete weekly rule.`);
        throw err;
    }
}

export async function createCalendarException(payload: Omit<CalendarExceptionRecord, 'id'> & { id?: string }) {
    try {
        const res = await transactionAxios.post(`dpr/apis/planning/calendar/exception`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error creating calendar exception: ${err}`);
        showErrorNotification(err, `Sorry! Failed to create exception.`);
        throw err;
    }
}

export async function updateCalendarException(calendarExceptionId: string, payload: Omit<CalendarExceptionRecord, 'id'>) {
    try {
        const res = await transactionAxios.put(`dpr/apis/planning/calendar/exception/${calendarExceptionId}`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error updating calendar exception: ${err}`);
        showErrorNotification(err, `Sorry! Failed to update exception.`);
        throw err;
    }
}

export async function deleteCalendarException(calendarExceptionId: string) {
    try {
        const res = await transactionAxios.delete(`dpr/apis/planning/calendar/exception/${calendarExceptionId}`);
        return res.data;
    } catch (err) {
        console.error(`Error deleting calendar exception: ${err}`);
        showErrorNotification(err, `Sorry! Failed to delete exception.`);
        throw err;
    }
}

export async function createCalendarDay(payload: Omit<CalendarDayRecord, 'id'> & { id?: string }) {
    try {
        const res = await transactionAxios.post(`dpr/apis/planning/calendar/day`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error creating calendar day override: ${err}`);
        showErrorNotification(err, `Sorry! Failed to create day override.`);
        throw err;
    }
}

export async function updateCalendarDay(calendarDayId: string, payload: Omit<CalendarDayRecord, 'id'>) {
    try {
        const res = await transactionAxios.put(`dpr/apis/planning/calendar/day/${calendarDayId}`, payload);
        return res.data;
    } catch (err) {
        console.error(`Error updating calendar day override: ${err}`);
        showErrorNotification(err, `Sorry! Failed to update day override.`);
        throw err;
    }
}

export async function deleteCalendarDay(calendarDayId: string) {
    try {
        const res = await transactionAxios.delete(`dpr/apis/planning/calendar/day/${calendarDayId}`);
        return res.data;
    } catch (err) {
        console.error(`Error deleting calendar day override: ${err}`);
        showErrorNotification(err, `Sorry! Failed to delete day override.`);
        throw err;
    }
}

attachSessionExpiryInterceptor(transactionAxios);
